import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useCamposObligatorios } from "../hooks/useCamposObligatorios";
import {
  Users,
  Loader2,
  UserPlus,
  UserMinus,
  Clock,
  Building2,
  Phone,
  Mail,
  FileText,
  Search,
  Calendar,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ArrowRightCircle,
  User,
  Briefcase,
  IdCard,
  Camera,
  RefreshCw
} from "lucide-react";

export default function ControlVisitantesPage() {
  const [visitantes, setVisitantes] = useState([]);
  const [tiposVisita, setTiposVisita] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegistroDialog, setShowRegistroDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("activos");
  const [saving, setSaving] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    visitasHoy: 0,
    visitantesActuales: 0,
    promedioTiempo: "0h 0m",
    visitasSemana: 0
  });
  
  // Form data for new visitor
  const [formData, setFormData] = useState({
    nombre: "",
    cedula: "",
    empresa: "",
    telefono: "",
    email: "",
    tipo_visita_id: "",
    departamento_id: "",
    persona_visita_id: "",
    persona_visita_otros: "", // Custom name when "Otros" is selected
    motivo: "",
    vehiculo_placa: "",
    vehiculo_marca: "",
    equipos: ""
  });
  
  // Alerts
  const [alertasCount, setAlertasCount] = useState(0);
  
  const { getAuthHeaders, user } = useAuth();
  
  // Hook para campos obligatorios
  const { isRequired, getLabel, validateForm } = useCamposObligatorios(
    "visitantes", 
    "registro_entrada", 
    getAuthHeaders
  );
  
  // Field definitions for validation
  const FIELD_DEFINITIONS = [
    { id: "nombre", label: "Nombre", defaultRequired: true },
    { id: "cedula", label: "Cédula", defaultRequired: false },
    { id: "empresa", label: "Empresa", defaultRequired: false },
    { id: "telefono", label: "Teléfono", defaultRequired: false },
    { id: "email", label: "Email", defaultRequired: false },
    { id: "tipo_visita_id", label: "Tipo de Visita", defaultRequired: true },
    { id: "departamento_id", label: "Departamento", defaultRequired: false },
    { id: "persona_visita_id", label: "Persona a Visitar", defaultRequired: false },
    { id: "motivo", label: "Motivo", defaultRequired: false }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [visitantesRes, tiposRes, deptRes, usersRes, statsRes, alertasRes] = await Promise.all([
        axios.get(`${API}/visitantes?estado=${filterEstado}`, getAuthHeaders()),
        axios.get(`${API}/visitantes/tipos`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/visitantes/stats`, getAuthHeaders()),
        axios.get(`${API}/visitantes/alertas`, getAuthHeaders())
      ]);
      
      setVisitantes(visitantesRes.data || []);
      setTiposVisita(tiposRes.data || []);
      setDepartamentos(deptRes.data || []);
      setUsuarios(usersRes.data || []);
      setStats(statsRes.data || {
        visitasHoy: 0,
        visitantesActuales: 0,
        promedioTiempo: "0h 0m",
        visitasSemana: 0
      });
      setAlertasCount(alertasRes.data?.total_alertas || 0);
    } catch (err) {
      toast.error("Error al cargar datos de visitantes");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterEstado]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegistrarEntrada = async () => {
    // Validate using dynamic campos obligatorios
    const validation = validateForm(formData, FIELD_DEFINITIONS);
    if (!validation.isValid) {
      toast.error(validation.errors[0]?.message || "Complete los campos requeridos");
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`${API}/visitantes/entrada`, formData, getAuthHeaders());
      toast.success(`Entrada registrada para ${formData.nombre}`);
      setShowRegistroDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar entrada");
    } finally {
      setSaving(false);
    }
  };

  const handleRegistrarSalida = async (visitanteId) => {
    try {
      await axios.post(`${API}/visitantes/${visitanteId}/salida`, {}, getAuthHeaders());
      toast.success("Salida registrada correctamente");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar salida");
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      cedula: "",
      empresa: "",
      telefono: "",
      email: "",
      tipo_visita_id: "",
      departamento_id: "",
      persona_visita_id: "",
      persona_visita_otros: "",
      motivo: "",
      vehiculo_placa: "",
      vehiculo_marca: "",
      equipos: ""
    });
  };

  const filteredVisitantes = visitantes.filter(v => {
    const search = searchTerm.toLowerCase();
    return (
      v.nombre?.toLowerCase().includes(search) ||
      v.cedula?.toLowerCase().includes(search) ||
      v.empresa?.toLowerCase().includes(search)
    );
  });

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-DO");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="control-visitantes-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Control de Visitantes
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registro y seguimiento de visitas
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button 
              onClick={() => setShowRegistroDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="registrar-entrada-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Registrar Entrada
            </Button>
          </div>
        </div>

        {/* Alert Banner */}
        {alertasCount > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-semibold text-red-800">
                  {alertasCount} visitante{alertasCount > 1 ? 's' : ''} con tiempo excedido
                </p>
                <p className="text-sm text-red-600">
                  Hay visitantes que han superado el tiempo límite de permanencia
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => window.location.href = '/visitantes/alertas'}
            >
              Ver Alertas
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-indigo-200 bg-indigo-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">Visitas Hoy</p>
                  <p className="text-3xl font-bold text-indigo-700">{stats.visitasHoy}</p>
                </div>
                <Calendar className="w-10 h-10 text-indigo-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">En el Edificio</p>
                  <p className="text-3xl font-bold text-green-700">{stats.visitantesActuales}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Tiempo Promedio</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.promedioTiempo}</p>
                </div>
                <Clock className="w-10 h-10 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Esta Semana</p>
                  <p className="text-3xl font-bold text-purple-700">{stats.visitasSemana}</p>
                </div>
                <Users className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, cédula o empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-visitantes"
                />
              </div>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activos">Actualmente Aquí</SelectItem>
                  <SelectItem value="hoy">Todas las de Hoy</SelectItem>
                  <SelectItem value="todos">Todas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Visitantes List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Registro de Visitantes
            </CardTitle>
            <CardDescription>
              {filteredVisitantes.length} visitante(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredVisitantes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No hay visitantes registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 font-medium text-slate-600">Visitante</th>
                      <th className="text-left p-3 font-medium text-slate-600">Cédula</th>
                      <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                      <th className="text-left p-3 font-medium text-slate-600">Visita a</th>
                      <th className="text-left p-3 font-medium text-slate-600">Motivo</th>
                      <th className="text-left p-3 font-medium text-slate-600">Entrada</th>
                      <th className="text-left p-3 font-medium text-slate-600">Salida</th>
                      <th className="text-left p-3 font-medium text-slate-600">Estado</th>
                      <th className="text-left p-3 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitantes.map((v) => (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`visitante-row-${v.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-indigo-600" />
                            </div>
                            <span className="font-medium">{v.nombre}</span>
                          </div>
                        </td>
                        <td className="p-3 font-mono text-sm">{v.cedula}</td>
                        <td className="p-3">{v.empresa || "-"}</td>
                        <td className="p-3">{v.persona_visita_nombre || v.departamento_nombre || "-"}</td>
                        <td className="p-3">
                          <Badge variant="outline">{v.tipo_visita_nombre || v.motivo || "-"}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            <div>{formatDate(v.fecha_entrada)}</div>
                            <div className="text-slate-500">{formatTime(v.fecha_entrada)}</div>
                          </div>
                        </td>
                        <td className="p-3">
                          {v.fecha_salida ? (
                            <div className="text-sm">
                              <div>{formatTime(v.fecha_salida)}</div>
                            </div>
                          ) : "-"}
                        </td>
                        <td className="p-3">
                          {v.fecha_salida ? (
                            <Badge className="bg-slate-100 text-slate-600">Salió</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">En edificio</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {!v.fecha_salida && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleRegistrarSalida(v.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`salida-btn-${v.id}`}
                            >
                              <UserMinus className="w-4 h-4 mr-1" />
                              Salida
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registro Dialog */}
        <Dialog open={showRegistroDialog} onOpenChange={setShowRegistroDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                Registrar Entrada de Visitante
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Datos Personales */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <IdCard className="w-4 h-4" />
                  Datos Personales
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{getLabel("nombre", "Nombre Completo", true)}</Label>
                    <Input
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Nombre del visitante"
                      data-testid="input-nombre"
                      required={isRequired("nombre", true)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{getLabel("cedula", "Cédula / Documento", false)}</Label>
                    <Input
                      value={formData.cedula}
                      onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                      placeholder="000-0000000-0"
                      data-testid="input-cedula"
                      required={isRequired("cedula", false)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{getLabel("telefono", "Teléfono", false)}</Label>
                    <Input
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      placeholder="809-000-0000"
                      required={isRequired("telefono", false)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{getLabel("email", "Email", false)}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@ejemplo.com"
                      required={isRequired("email", false)}
                    />
                  </div>
                </div>
              </div>

              {/* Datos de la Empresa */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Empresa / Procedencia
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>{getLabel("empresa", "Empresa que Representa", false)}</Label>
                    <Input
                      value={formData.empresa}
                      onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                      placeholder="Nombre de la empresa"
                      required={isRequired("empresa", false)}
                    />
                  </div>
                </div>
              </div>

              {/* Datos de la Visita */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <ArrowRightCircle className="w-4 h-4" />
                  Datos de la Visita
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{getLabel("tipo_visita_id", "Tipo de Visita", true)}</Label>
                    <Select 
                      value={formData.tipo_visita_id} 
                      onValueChange={(v) => setFormData({ ...formData, tipo_visita_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposVisita.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Departamento a Visitar</Label>
                    <Select 
                      value={formData.departamento_id} 
                      onValueChange={(v) => setFormData({ ...formData, departamento_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar departamento..." />
                      </SelectTrigger>
                      <SelectContent>
                        {departamentos.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Persona a Visitar</Label>
                    <Select 
                      value={formData.persona_visita_id} 
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        persona_visita_id: v,
                        persona_visita_otros: v === "otros" ? formData.persona_visita_otros : ""
                      })}
                    >
                      <SelectTrigger data-testid="select-persona-visita">
                        <SelectValue placeholder="Seleccionar persona..." />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Mostrar usuarios filtrados por departamento si hay departamento seleccionado */}
                        {/* Si no hay departamento seleccionado, mostrar todos */}
                        {/* Si un usuario no tiene departamento asignado, mostrarlo siempre */}
                        {usuarios.filter(u => {
                          // Si no hay departamento seleccionado, mostrar todos
                          if (!formData.departamento_id) return true;
                          // Si el usuario no tiene departamento, mostrarlo (para que pueda ser asignado)
                          if (!u.departamento_id && !u.department) return true;
                          // Filtrar por departamento
                          return u.departamento_id === formData.departamento_id ||
                                 u.department === formData.departamento_id;
                        }).map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                        ))}
                        <SelectItem value="otros" className="font-medium text-indigo-600">
                          Otros (especificar)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.persona_visita_id === "otros" && (
                    <div className="col-span-2 space-y-2">
                      <Label>Nombre de la Persona a Visitar</Label>
                      <Input
                        value={formData.persona_visita_otros}
                        onChange={(e) => setFormData({ ...formData, persona_visita_otros: e.target.value })}
                        placeholder="Ingrese el nombre de la persona..."
                        data-testid="input-persona-otros"
                        className="border-indigo-200 focus:border-indigo-500"
                      />
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Motivo de la Visita</Label>
                    <Textarea
                      value={formData.motivo}
                      onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                      placeholder="Describa el motivo de la visita..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Datos de Vehículo y Equipos (opcional) */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Información Adicional (Opcional)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Placa del Vehículo</Label>
                    <Input
                      value={formData.vehiculo_placa}
                      onChange={(e) => setFormData({ ...formData, vehiculo_placa: e.target.value })}
                      placeholder="Ej: A123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Marca del Vehículo</Label>
                    <Input
                      value={formData.vehiculo_marca}
                      onChange={(e) => setFormData({ ...formData, vehiculo_marca: e.target.value })}
                      placeholder="Ej: Toyota Corolla"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Equipos que Porta</Label>
                    <Input
                      value={formData.equipos}
                      onChange={(e) => setFormData({ ...formData, equipos: e.target.value })}
                      placeholder="Laptop, herramientas, etc."
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowRegistroDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleRegistrarEntrada} 
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirmar-entrada-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Registrar Entrada
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
